import { log } from "./log.js";
import { appendTokenIfPresent } from "./url-token.js";
/**
 * Backend-driven interop — the SOLE {@link EditorInterop} implementation as of
 * the pdf-editor-no-itext Epic Phase 4 (retrofit design §2.3/§2.5):
 *
 *   - `loadPdf` / `getStructTree` are near-no-ops: the authoritative struct tree
 *     is fetched by `loadAuthoritativeIR` (editor-load-flow) directly from the
 *     backend `editor-ir` endpoint, NOT through this seam. This interop exists to
 *     serve the OVERLAY-GEOMETRY (bbox) + DOWNLOAD paths.
 *   - `getBoundingBoxes(Range)` reads the per-block `bbox` off the injected IR
 *     (§G-Q2 — IR bboxes, no content-stream re-walk). The IR bbox is in the same
 *     PDF user-space `[x,y,w,h]` tuple the overlay/bbox-cache consume (see
 *     `ir-bbox-sync.reconcileBboxesWithIr`).
 *   - `getAttestation` reads the attestation off the IR meta (Phase 2 folded
 *     `PdfAttestationReader.Read` into the IR).
 *   - `applyOp` is an optimistic CLIENT ECHO only — the authoritative mutation is
 *     the SAVE round-trip (backend `editor-edit`); this returns `{ ok: true }` so
 *     the chrome's optimistic UI proceeds.
 *   - `serializeToBytes` fetches the current SAVE checkpoint via
 *     `GET /api/editor-download/<jobId>` — no client-side serializer.
 */
export class BackendEditorInterop {
    _jobId;
    _irProvider;
    _fetch;
    _loaded;
    _listeners;
    constructor(jobId, irProvider, fetchImpl) {
        this._jobId = jobId;
        this._irProvider = irProvider;
        this._fetch = fetchImpl;
        this._loaded = false;
        this._listeners = new Map();
    }
    // The bytes are already parsed authoritatively by loadAuthoritativeIR; this
    // just flips the loaded flag so subsequent getBoundingBoxes/serialize calls
    // don't throw PdfNotLoaded (matching the WASM lifecycle contract).
    // eslint-disable-next-line @typescript-eslint/require-await -- signature parity with WASM impl (chrome awaits this)
    async loadPdf(bytes, _fileName) {
        if (!(bytes instanceof Uint8Array)) {
            throw new TypeError("loadPdf expects a Uint8Array");
        }
        this._loaded = true;
        const ir = this._irProvider();
        return { ok: true, hasStructTree: !!(ir && ir.blocks.length > 0) };
    }
    // The authoritative struct tree is the backend IR. This seam derives the
    // WASM-shaped `state.structTree` (the overlay ROLE-MAP source) from the live
    // IR so overlays paint roles on the backend path. At Tier-1 the IR is not yet
    // installed (it loads in Tier-2), so this returns an empty tree; the load
    // flow REFRESHES `state.structTree` from this seam after the Tier-2 IR
    // install, at which point the nodes are populated. Retrofit design §2.3.
    // eslint-disable-next-line @typescript-eslint/require-await -- signature parity with WASM impl
    async getStructTree() {
        if (!this._loaded)
            throw new Error("PdfNotLoaded");
        const ir = this._irProvider();
        const nodes = ir ? buildChromeNodesFromIr(ir) : [];
        return {
            nodes,
            parentPointers: nodes.map(() => null),
            docTitle: null,
            docLang: null,
            format: "PDF",
            byId: ir ? buildIrByIdMap(ir) : null,
        };
    }
    // eslint-disable-next-line @typescript-eslint/require-await -- signature parity with WASM impl
    async getAttestation() {
        if (!this._loaded)
            throw new Error("PdfNotLoaded");
        const ir = this._irProvider();
        return ir?.attestation ?? null;
    }
    async getBoundingBoxes() {
        // -1 = to end of document (matches the WASM range sentinel).
        return this.getBoundingBoxesRange(0, -1);
    }
    async getBoundingBoxesForPage(pageNum) {
        const idx0 = pageNum - 1;
        return this.getBoundingBoxesRange(idx0, idx0);
    }
    /**
     * Read per-block bboxes off the IR for pages [pageStart .. pageEnd] (0-based,
     * inclusive; -1 = to end of doc). Returns only blocks that carry a bbox — the
     * WASM path likewise emitted an entry only for struct elements with
     * renderable geometry. Coordinate space is the PDF user-space `[x,y,w,h]`
     * tuple (identical to the WASM contract; see `ir-bbox-sync`).
     */
    // eslint-disable-next-line @typescript-eslint/require-await -- signature parity with WASM impl (bbox-cache awaits this)
    async getBoundingBoxesRange(pageStart, pageEnd) {
        if (!this._loaded)
            throw new Error("PdfNotLoaded");
        const ir = this._irProvider();
        if (!ir || !Array.isArray(ir.blocks))
            return [];
        const loPage = pageStart + 1;
        const hiPage = pageEnd < 0 ? Infinity : pageEnd + 1;
        const out = [];
        collectIrBboxes(ir.blocks, loPage, hiPage, out);
        return out;
    }
    // Optimistic client echo only. The authoritative mutation is the SAVE
    // round-trip (backend `editor-edit`); the chrome POSTs the op-log on save.
    // eslint-disable-next-line @typescript-eslint/require-await -- signature parity with WASM impl
    async applyOp(op) {
        if (!this._loaded)
            throw new Error("PdfNotLoaded");
        const result = { ok: true };
        this._fire("op-applied", { op, result });
        return result;
    }
    /**
     * Fetch the current SAVE checkpoint (`output.editor-edited.pdf`) via
     * `GET /api/editor-download/<jobId>`. No client serializer — the backend SAVE
     * already wrote the artifact. Unlike the WASM serializer this does NOT close
     * the document, so `_loaded` stays true (the WASM path flipped it false).
     */
    async serializeToBytes() {
        if (!this._loaded)
            throw new Error("PdfNotLoaded");
        const url = appendTokenIfPresent(`/api/editor-download/${encodeURIComponent(this._jobId)}`);
        const resp = await this._fetch(url, { credentials: "same-origin" });
        if (!resp.ok) {
            throw new Error(`editor-download failed: HTTP ${resp.status}`);
        }
        const buf = await resp.arrayBuffer();
        return new Uint8Array(buf);
    }
    on(event, callback) {
        if (!this._listeners.has(event))
            this._listeners.set(event, new Set());
        this._listeners.get(event).add(callback);
        return () => this._listeners.get(event)?.delete(callback);
    }
    _fire(event, payload) {
        const set = this._listeners.get(event);
        if (!set)
            return;
        for (const cb of set) {
            try {
                cb(payload);
            }
            catch (err) {
                // best-effort listener dispatch; one bad listener shouldn't break the rest
                log.error("[backend-interop] listener error:", err);
            }
        }
    }
}
/**
 * Walk IR blocks (recursing into List items) collecting bboxes for pages in
 * [loPage .. hiPage] (1-based, inclusive). Mirrors `ir-bbox-sync.buildIrBboxIndex`
 * — the same IR-bbox → tuple conversion, so the backend interop and the sync
 * layer stay coordinate-space-consistent.
 */
function collectIrBboxes(blocks, loPage, hiPage, out) {
    for (const block of blocks) {
        if (!block)
            continue;
        const bbox = block.bbox;
        if (block.structId && bbox && block.page >= loPage && block.page <= hiPage) {
            out.push({
                structId: block.structId,
                page: block.page,
                bbox: [bbox.x, bbox.y, bbox.w, bbox.h],
            });
        }
        if (block.kind === "List" && Array.isArray(block.items)) {
            for (const item of block.items) {
                if (Array.isArray(item.content)) {
                    collectIrBboxes(item.content, loPage, hiPage, out);
                }
            }
        }
    }
}
/**
 * Build the WASM-shaped `ChromeStructNode[]` from the IR. The `role` is the IR
 * block `kind` (Heading/Paragraph/Figure/…), which is exactly what
 * `overlays.buildRoleMap` keys on. Figures carry `altText` / `isDecorative`;
 * other blocks stub them (parity with `adaptStructNode`, which stubs
 * `isDecorative=false` for the WASM path). Recurses into List items so nested
 * LIs get overlay roles. Retrofit design §2.3.
 */
function buildChromeNodesFromIr(ir) {
    const out = [];
    const visit = (blocks) => {
        for (const block of blocks) {
            if (!block?.structId)
                continue;
            const isFigure = block.kind === "Figure";
            out.push({
                structId: block.structId,
                role: block.kind,
                text: "",
                page: block.page,
                altText: isFigure ? block.alt : null,
                isDecorative: isFigure ? block.decorative : false,
            });
            if (block.kind === "List" && Array.isArray(block.items)) {
                for (const item of block.items) {
                    if (Array.isArray(item.content))
                        visit(item.content);
                }
            }
        }
    };
    if (Array.isArray(ir.blocks))
        visit(ir.blocks);
    return out;
}
/**
 * Build the overlay role map ({structId → role-kind}) directly from the IR.
 * The overlay badge render keys on this map; a MISSING id degrades to "?" for
 * THAT badge only (per-block resilience). This is the fallback the render path
 * uses when the server-derived struct tree is absent/stale so the map is never
 * GLOBALLY empty while an IR exists — the all-badges-"?" crash class
 * (editor-list-rca). `buildChromeNodesFromIr` already guards every block and
 * recurses List items, so a single malformed block cannot blank the whole map.
 */
export function buildRoleMapFromIr(ir) {
    const map = new Map();
    if (!ir)
        return map;
    for (const n of buildChromeNodesFromIr(ir)) {
        if (n.structId)
            map.set(n.structId, n.role || "");
    }
    return map;
}
/**
 * Build the `StructTree.byId` reverse-lookup ({structId → {page, mcids}}) from
 * the IR. The overlay snap-to-content path consumes this; on the backend load
 * path the IR is the source (the WASM struct-node list is empty).
 */
function buildIrByIdMap(ir) {
    const out = new Map();
    const visit = (blocks) => {
        for (const block of blocks) {
            if (!block?.structId)
                continue;
            out.set(block.structId, { page: block.page, mcids: null });
            if (block.kind === "List" && Array.isArray(block.items)) {
                for (const item of block.items) {
                    if (Array.isArray(item.content))
                        visit(item.content);
                }
            }
        }
    };
    if (Array.isArray(ir.blocks))
        visit(ir.blocks);
    return out;
}
/**
 * Construct the editor interop — always a {@link BackendEditorInterop} (fetch-
 * based, IR-driven; no WASM bundle, no Blazor bootstrap). The `jobId` is required
 * (the drag-drop entry registers a transient job before this runs, so every entry
 * is keyed). Retrofit design §2.3/§2.5.
 */
export async function createEditorInterop(opts) {
    const { jobId, irProvider, fetchImpl } = opts;
    if (!jobId) {
        // Fail loud: a jobId-less call is a caller bug now that the WASM fallback
        // is gone (drag-drop must register a transient job first — §G-Q1).
        throw new Error("createEditorInterop: jobId is required (no WASM fallback)");
    }
    const provider = irProvider ?? (() => null);
    const doFetch = fetchImpl
        ?? ((url, init) => fetch(url, init));
    return Promise.resolve(new BackendEditorInterop(jobId, provider, doFetch));
}
